<script setup lang="ts">
import BackgroundEffects from "~/components/Rebrand/Home/BackgroundEffects.vue";

// Get the error prop that Nuxt passes to the error page
const props = defineProps({
  error: Object
});

// Set the title based on error status
useHead({
  title: props.error?.statusCode === 404 
    ? 'Page Not Found - Fezz Digital Solutions' 
    : 'Error - Fezz Digital Solutions'
});

// Function to go back to home page
const handleGoHome = () => {
  clearError({ redirect: '/' });
};

// Function to go back to previous page
const handleGoBack = () => {
  // Use router to go back if possible, otherwise go home
  const router = useRouter();
  router.back();
};
</script>

<template>
  <div>
    <BackgroundEffects />
    <div class="pt-36 px-6 md:px-16 xl:px-32 bg-gray-950 min-h-screen flex flex-col items-center justify-center text-center">
      <div class="max-w-2xl mx-auto">
        <!-- Error Status Code -->
        <h1 class="text-8xl md:text-9xl font-bold text-purple-500 mb-4">
          {{ error?.statusCode || '404' }}
        </h1>
        
        <!-- Error Message -->
        <h2 class="text-3xl md:text-4xl font-bold text-white mb-6">
          {{ error?.statusCode === 404 ? 'Page Not Found' : 'Something went wrong' }}
        </h2>
        
        <p class="text-gray-300 text-xl mb-10">
          {{ 
            error?.statusCode === 404 
              ? "The page you're looking for doesn't exist or has been moved." 
              : "We encountered an error while processing your request." 
          }}
        </p>
        
        <div class="flex flex-col md:flex-row items-center justify-center gap-4">
          <!-- Go Back Button -->
          <button 
            @click="handleGoBack" 
            class="px-6 py-3 text-lg font-medium border-2 border-white/30 text-white bg-transparent rounded-sm transition-all duration-300 hover:bg-white/10"
          >
            ← Go Back
          </button>
          
          <!-- Home Button -->
          <button 
            @click="handleGoHome" 
            class="px-6 py-3 text-lg font-medium border-2 border-purple-500 text-purple-400 bg-transparent rounded-sm transition-all duration-500 ease-in-out hover:bg-purple-500 hover:text-white hover:shadow-purple-500/30 hover:shadow-lg"
          >
            Go Home
          </button>
        </div>
        
        <!-- Additional Details -->
        <div v-if="error && error.statusCode !== 404" class="mt-12 text-left">
          <details class="bg-gray-800/50 border border-gray-700 rounded-md p-4">
            <summary class="text-gray-300 cursor-pointer">Technical Details</summary>
            <pre class="text-sm text-gray-400 mt-4 p-4 bg-gray-800 rounded overflow-auto">{{ error }}</pre>
          </details>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
button {
  min-width: 160px;
}
</style>
